import { Role } from 'auth';
import { isAxiosError } from 'axios';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerPortfolioAITrackingModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAITrackingModal.tsx';
import { CashMovementsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/CashMovementsTab.tsx';
import { PortfolioAssetsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioAssetsTab.tsx';
import { PortfolioBankAccountsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioBankAccountsTab.tsx';
import { PortfolioBrokersTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioBrokersTab.tsx';
import { PortfolioOverviewTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioOverviewTab.tsx';
import { PortfolioSourceEmailsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioSourceEmailsTab.tsx';
import { PortfolioTransactionsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import {
  useIssuerOutdatedAssetsQuery,
  useIssuerPortfolioByIdQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  Button,
  ModalProviderContext,
  PageLayout,
  Skeleton,
  Spinner,
  TabItem,
  Tabs,
} from 'ui';
import { AIIcon, AlertOrangeIcon } from 'ui/icons';

enum PortfolioTabsEnum {
  Overview = 'Overview',
  PortfolioTransactions = 'Portfolio transactions',
  CashMovements = 'Cash movements',
  Assets = 'Assets',
  BankAccounts = 'Bank accounts',
  Brokers = 'Brokers',
  SourceEmails = 'Source emails',
}

const PORTFOLIO_TAB_NAME = 'tab-portfolio';

const IssuerPortfolio = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const portfolioId = Number(id);
  const hasValidPortfolioId = Number.isSafeInteger(portfolioId) && portfolioId > 0;
  const paramTab = params.get(PORTFOLIO_TAB_NAME);
  const isValidTab =
    paramTab && Object.values(PortfolioTabsEnum).includes(paramTab as PortfolioTabsEnum);
  const [activeTab, setActiveTab] = useState<PortfolioTabsEnum>(
    isValidTab ? (paramTab as PortfolioTabsEnum) : PortfolioTabsEnum.Overview,
  );

  const {
    data: dataPortfolio,
    isLoading,
    error,
    isError,
    isFetching,
    refetch,
  } = useIssuerPortfolioByIdQuery({ portfolioId }, !hasValidPortfolioId);

  const { data: outdatedAssetsData } = useIssuerOutdatedAssetsQuery(!hasValidPortfolioId);

  const outdatedAssetIds = useMemo<Set<number>>(
    () => new Set((outdatedAssetsData?.outdated_assets ?? []).map(a => a.id)),
    [outdatedAssetsData],
  );

  const tabsItems = useMemo<TabItem<PortfolioTabsEnum>[]>(
    () => [
      {
        name: PortfolioTabsEnum.Overview,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioOverviewTab portfolio={dataPortfolio?.portfolio} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.PortfolioTransactions,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioTransactionsTab portfolioId={dataPortfolio?.portfolio?.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.CashMovements,
        container: dataPortfolio?.portfolio?.id ? (
          <CashMovementsTab portfolioId={dataPortfolio.portfolio.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      // { name: PortfolioTabsEnum.BankTransactions, container: <PortfolioBankTransactionsTab /> },
      // {
      //   name: PortfolioTabsEnum.InvestorTransactions,
      //   container: dataPortfolio?.portfolio?.id ? (
      //     <PortfolioInvestorTransactionsTab portfolioId={dataPortfolio?.portfolio?.id} />
      //   ) : (
      //     <div className="flex flex-col w-full h-[250px] justify-center items-center">
      //       <Spinner size="lg" />
      //     </div>
      //   ),
      // },
      {
        name: PortfolioTabsEnum.Assets,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioAssetsTab
            portfolioId={dataPortfolio?.portfolio?.id}
            outdatedAssetIds={outdatedAssetIds}
          />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.BankAccounts,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioBankAccountsTab />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.Brokers,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioBrokersTab portfolioId={dataPortfolio?.portfolio?.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
        // disabled: true,
        // disabledReason: 'We are working on it',
      },
      {
        name: PortfolioTabsEnum.SourceEmails,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioSourceEmailsTab portfolioId={dataPortfolio.portfolio.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
    ],
    [dataPortfolio?.portfolio, outdatedAssetIds],
  );

  if (!hasValidPortfolioId || isError) {
    const response = isAxiosError(error) ? error.response : undefined;
    const status = response?.status;
    const message = response?.data?.error_message;
    const title =
      !hasValidPortfolioId || status === 404
        ? 'Portfolio not found'
        : status === 403
        ? 'Portfolio access unavailable'
        : "We couldn't load this portfolio";

    return (
      <PageLayout title="Custody">
        <div className="flex min-h-[320px] flex-1 items-center justify-center py-8">
          <div
            role="alert"
            className="flex w-full max-w-lg flex-col items-center gap-4 rounded-xl border border-gray-200 bg-white p-6 text-center sm:p-8"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-50">
              <img src={AlertOrangeIcon} alt="" className="h-7 w-7" />
            </div>
            <div className="space-y-2">
              <h2 className="font-serif text-2xl text-gray-900">{title}</h2>
              <p className="text-sm leading-6 text-gray-500">
                {!hasValidPortfolioId || status === 404
                  ? 'This portfolio may have been removed or the link is incorrect.'
                  : status === 403
                  ? 'You do not have permission to view this portfolio. Please contact your administrator.'
                  : 'Please try again. If the problem persists, contact support.'}
              </p>
            </div>
            {typeof message === 'string' && message.trim() && (
              <p className="w-full break-words rounded-lg bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-600 [overflow-wrap:anywhere]">
                {message}
              </p>
            )}
            <div className="flex w-full flex-col justify-center gap-3 pt-2 sm:w-auto sm:flex-row">
              {hasValidPortfolioId && (
                <Button
                  className="w-full sm:w-auto"
                  loading={isFetching}
                  onClick={() => void refetch()}
                >
                  Retry
                </Button>
              )}
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => {
                  const dashboardParams = new URLSearchParams(params);
                  dashboardParams.delete(PORTFOLIO_TAB_NAME);
                  navigate({
                    pathname: issuerDashboardPaths.static.dashboard,
                    search: dashboardParams.toString(),
                  });
                }}
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Custody"
      tabs={tabsItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={PORTFOLIO_TAB_NAME}
      contentClassName="px-0 py-2"
      headerBottomContent={
        outdatedAssetsData?.has_outdated ? (
          <Alert
            variant="neutral-2"
            className="mb-0"
            icon={AlertOrangeIcon}
            label="Some assets have not had their prices updated in over 30 days. Please update prices before calculating NAV."
          />
        ) : undefined
      }
      headerAction={
        <div className="flex items-center gap-4 select-none">
          {isLoading ? (
            <>
              <Skeleton width={32} height={32} />
            </>
          ) : (
            <>
              <Button
                variant={'secondary'}
                className="py-1 px-2 min-h-8"
                icon={AIIcon}
                onClick={() => {
                  openModal(IssuerPortfolioAITrackingModal);
                }}
              >
                AI Tracking
              </Button>
            </>
          )}
        </div>
      }
    >
      <Tabs items={tabsItems} activeTab={activeTab} />
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerPortfolio, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
